/**
 * apps/web :: hooks/useHasAccess
 *
 * React hook that wraps `GET /api/entitlements/me/check` (ROADS-445 FE-01).
 * Used by the route gates to decide between rendering content and the
 * paywall.
 *
 * Behaviour:
 *   - state === "loading"        → first paint while the check is in flight.
 *                                  Render a spinner; never render content.
 *   - state === "ready" + true   → user holds a matching active entitlement;
 *                                  render the gated content.
 *   - state === "ready" + false  → user is not entitled; render the paywall.
 *   - state === "error"          → network / 5xx; surface a soft error.
 *                                  401s never land here — they bubble up
 *                                  as `ApiError` and the caller's
 *                                  ProtectedRoute handles the redirect to
 *                                  `/login`.
 *
 * The hook is the right hand side of the trial decision tree:
 *
 *     trial.active === true  → render content (free trial)
 *     trial.active === false → call useHasAccess(...)
 *                              has_access === true  → render content
 *                              has_access === false → render paywall
 */
import { useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import {
  checkAccess,
  type EntitlementCheckResponse,
  type EntitlementTier,
} from "@/lib/entitlement-api";

export type UseHasAccessState =
  | { kind: "loading" }
  | { kind: "ready"; hasAccess: boolean; response: EntitlementCheckResponse }
  | { kind: "error"; status: number | null; message: string };

export interface UseHasAccessParams {
  tier: EntitlementTier;
  /** USPS state code (e.g. "CA"). Required for state-scoped tiers. */
  state?: string | null;
  /** ISO 3166-1 alpha-2 (e.g. "GB"). Required for tourist_out. */
  country?: string | null;
  /**
   * Skip the network call (and emit a deterministic ready state) when
   * `false`. Used by the trial gate so that, when the trial is still
   * active, we don't burn a request only to ignore the result.
   */
  enabled?: boolean;
}

/**
 * Default ready state used when `enabled` is false. We treat
 * `enabled=false` as "the caller already decided access is granted"
 * (e.g. trial active) — returning hasAccess=true keeps the consumer
 * branching simple.
 */
function readyEnabledFalse(
  tier: EntitlementTier,
  state: string | null,
  country: string | null,
): UseHasAccessState {
  return {
    kind: "ready",
    hasAccess: true,
    response: {
      has_access: true,
      tier,
      state_code: state,
      country_code: country,
      now_ms: Date.now(),
    },
  };
}

export function useHasAccess(params: UseHasAccessParams): UseHasAccessState {
  const { tier, state = null, country = null, enabled = true } = params;
  const [result, setResult] = useState<UseHasAccessState>(
    enabled ? { kind: "loading" } : readyEnabledFalse(tier, state, country),
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setResult(readyEnabledFalse(tier, state, country));
      return;
    }
    setResult({ kind: "loading" });
    let cancelled = false;
    void (async () => {
      try {
        const response = await checkAccess({ tier, state, country });
        if (cancelled || !mountedRef.current) return;
        setResult({
          kind: "ready",
          hasAccess: response.has_access,
          response,
        });
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        if (err instanceof ApiError) {
          setResult({
            kind: "error",
            status: err.status,
            message: err.message,
          });
          return;
        }
        setResult({
          kind: "error",
          status: null,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tier, state, country, enabled]);

  return result;
}
